import React, { useEffect, useState } from 'react';
import { appointmentService, Service, ServiceCategory } from '../services/appointmentService';
import ServiceForm from './ServiceForm';
import ServiceCategoryModal from './ServiceCategoryModal';
import { Edit2, Trash2, Plus } from 'lucide-react';
import { formatCurrency } from '../utils';

interface Props {
  branch?: 'antofagasta' | 'santiago';
}

const ServiceList: React.FC<Props> = ({ branch = 'antofagasta' }) => {
  const [categories, setCategories] = useState<ServiceCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingService, setEditingService] = useState<Partial<Service> | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState<ServiceCategory | null>(null);
  const [showCategoryModal, setShowCategoryModal] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = branch === 'santiago'
        ? await appointmentService.getAllCategoriesSantiago()
        : await appointmentService.getAllCategories();
      setCategories(res || []);
    } catch (err) {
      console.error('Error loading service categories', err);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [branch]);

  const handleCreateClick = (categoryId?: number) => {
    setEditingService({ category: categoryId } as any);
    setShowForm(true);
  };

  const handleEdit = (svc: Service) => {
    setEditingService(svc);
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('¿Eliminar este servicio?')) return;
    try {
      if (branch === 'santiago') {
        await appointmentService.deleteServiceSantiago(id);
      } else {
        await appointmentService.deleteService(id);
      }
      await load();
    } catch (err) {
      console.error('Error deleting service', err);
    }
  };

  const handleSaved = async (saved: Service) => {
    setShowForm(false);
    setEditingService(null);
    await load();
  };

  const handleCategoryCreated = async () => {
    await load();
  };

  const handleCategoryUpdated = async () => {
    setEditingCategory(null);
    setShowCategoryModal(false);
    await load();
  };

  const handleEditCategory = (cat: ServiceCategory) => {
    setEditingCategory(cat);
    setShowCategoryModal(true);
  };

  const handleDeleteCategory = async (id: number) => {
    if (!window.confirm('¿Estás seguro de eliminar esta categoría? Todos los servicios asociados también serán eliminados.')) return;
    try {
      if (branch === 'santiago') {
        await appointmentService.deleteCategorySantiago(id);
      } else {
        await appointmentService.deleteCategory(id);
      }
      await load();
    } catch (err) {
      console.error('Error deleting category', err);
      alert('Error al eliminar la categoría. Verifica que no tenga servicios asociados.');
    }
  };

  const handleCloseCategoryModal = () => {
    setEditingCategory(null);
    setShowCategoryModal(false);
  };

  if (loading) return <div className="p-6 bg-white rounded-xl shadow-sm border border-gray-200">Cargando servicios...</div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold">Servicios</h2>
        <div className="flex items-center space-x-2">
          <ServiceCategoryModal onCreated={handleCategoryCreated} branch={branch} />
          <button onClick={() => handleCreateClick()} className="inline-flex items-center space-x-2 px-4 py-2 bg-gray-900 text-white rounded-lg"><Plus className="w-4 h-4" /><span>Nuevo Servicio</span></button>
        </div>
      </div>

      {showForm && (
        <ServiceForm 
          service={editingService} 
          categories={categories} 
          onSaved={handleSaved} 
          onCancel={() => { setShowForm(false); setEditingService(null); }} 
          branch={branch}
        />
      )}

      <ServiceCategoryModal 
        category={editingCategory || undefined}
        onCreated={handleCategoryCreated}
        onUpdated={handleCategoryUpdated}
        onClose={handleCloseCategoryModal}
        isOpen={showCategoryModal}
        branch={branch}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {categories.map(cat => (
          <div key={cat.id} className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex-1">
                <div className="flex items-center space-x-2">
                  <h3 className="text-md font-semibold">{cat.name}</h3>
                  {cat.image && (
                    <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Con imagen</span>
                  )}
                </div>
                <p className="text-sm text-gray-500">{(cat.services || []).length} servicios</p>
              </div>
              <div className="flex items-center space-x-2">
                <button 
                  onClick={() => handleEditCategory(cat)}
                  className="px-3 py-1.5 bg-indigo-50 text-indigo-700 rounded-lg border border-indigo-200 inline-flex items-center hover:bg-indigo-100 transition-colors"
                  title="Editar categoría"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
                <button 
                  onClick={() => handleDeleteCategory(cat.id)}
                  className="px-3 py-1.5 bg-red-50 text-red-700 rounded-lg border border-red-200 inline-flex items-center hover:bg-red-100 transition-colors"
                  title="Eliminar categoría"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
                <button 
                  onClick={() => handleCreateClick(cat.id)} 
                  className="inline-flex items-center space-x-2 px-4 py-2 text-sm bg-blue-600 text-white rounded-none hover:bg-blue-700 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  <span>Agregar</span>
                </button>
              </div>
            </div>

            <ul className="divide-y divide-gray-100">
              {(cat.services || []).map((s: Service) => (
                <li key={s.id} className="py-3 flex items-center justify-between">
                  <div>
                    <div className="font-medium">{s.name}</div>
                    <div className="text-sm text-gray-500">{s.duration_minutes} min • {formatCurrency(Number(s.price))}</div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button onClick={() => handleEdit(s)} className="inline-flex items-center px-3 py-1.5 bg-blue-600 text-white rounded-none hover:bg-blue-700"><Edit2 className="w-4 h-4" /></button>
                    <button onClick={() => handleDelete(s.id)} className="inline-flex items-center px-3 py-1.5 bg-red-600 text-white rounded-none hover:bg-red-700"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ServiceList;
